/**
 * Stockage des tokens OAuth.
 *
 * - Tokens persistants (refresh + access + email) → IndexedDB via idb-keyval.
 *   Survit aux refresh, à la fermeture de l'onglet, à un reboot device.
 *   Origin-isolé (m.autonao.com vs localhost = stockages séparés → bonne isolation
 *   dev/prod automatique).
 *
 * - Pending auth (state + code_verifier pendant le redirect Google) → sessionStorage.
 *   TTL = onglet courant. Survit au navigate vers Google + retour. Synchrone, simple.
 */

import { get, set, del } from "idb-keyval";

const TOKEN_KEY = "mailfox:auth:tokens";
const PENDING_KEY = "mailfox:auth:pending";

export interface AuthTokens {
  /** Long-lived (jusqu'à 6 mois inactif), permet de refresh access_token. */
  refresh_token: string;
  /** Short-lived (1h chez Google). À régénérer via /oauth/refresh quand expiré. */
  access_token: string;
  /** Unix ms — `Date.now() < expires_at` = encore valide. -60s safety margin. */
  expires_at: number;
  /** Email du user (extrait de id_token). Stable, pas besoin de re-fetch. */
  email: string;
  /** JWT signé par Google contenant les claims user. Conservé pour future verif. */
  id_token: string;
}

export interface PendingAuth {
  state: string;
  code_verifier: string;
  redirect_uri: string;
  created_at: number;
}

export async function saveTokens(t: AuthTokens): Promise<void> {
  await set(TOKEN_KEY, t);
}

export async function loadTokens(): Promise<AuthTokens | null> {
  const v = await get<AuthTokens>(TOKEN_KEY);
  return v ?? null;
}

export async function clearTokens(): Promise<void> {
  await del(TOKEN_KEY);
}

export function savePending(p: PendingAuth): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
}

export function loadPending(): PendingAuth | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingAuth;
  } catch {
    return null;
  }
}

export function clearPending(): void {
  sessionStorage.removeItem(PENDING_KEY);
}
